import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { MRT_Row, MRT_TableInstance } from "material-react-table";
import { useIntl } from "react-intl";
import {
  SkuQualificationGroupBy,
  SkuQualificationGroupModel,
  SkuQualificationRow,
  MULTIPLE_VALUES_INDICATOR,
} from "helpers/skuQualificationHelpers";
import SkuQualificationStatusChip from "./SkuQualificationStatusChip";
import ChildRow, { ColumnConfig } from "./ChildRow";

type ChildCellRenderer = (qual: SkuQualificationRow) => React.ReactNode;

// Columns the group key pins to a single value for every member, so repeating them on each
// child row adds nothing. Profession and validity are part of the key in every view; By
// Licensure Body keys on the whole LB/AB/SA/Profession identity, so all four are pinned
// there and only the scalars (course number, dates, credits) vary between members.
const REDUNDANT_COLUMN_IDS: Partial<Record<SkuQualificationGroupBy, string[]>> = {
  licensureBody: [
    "status",
    "licensureBody",
    "accreditationBody",
    "submissionAuthority",
    "profession.name",
  ],
  accreditationBody: ["status", "accreditationBody", "profession.name"],
  submissionAuthority: ["status", "submissionAuthority", "profession.name"],
};

// Read-only by design: a member row is edited and deleted from its parent group row,
// so the detail panel carries no actions column.
type ExpandedChildTableProps = {
  readonly row: MRT_Row<SkuQualificationGroupModel>;
  readonly table: MRT_TableInstance<SkuQualificationGroupModel>;
  readonly groupBy: SkuQualificationGroupBy;
};

const styles = {
  parentNotes: {
    fontSize: "0.875rem",
    color: "text.secondary",
  },
};

const formatDate = (date: Date | undefined | null) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString();
};

export default function ExpandedChildTable({
  row,
  table,
  groupBy,
}: ExpandedChildTableProps) {
  const { formatMessage: t } = useIntl();
  const parent = row.original;

  if (!parent.isGroup || !parent.childQualifications) return null;

  const children = parent.childQualifications;

  if (children.length === 0) return null;

  // One renderer per parent column id. A member holds a single value on every axis, so
  // the roll-up cells the parent uses (chips, "Multiple Values") resolve to the plain
  // value here. Columns with no entry (the expand toggle, row actions) are skipped.
  const childCells: Record<string, ChildCellRenderer | undefined> = {
    status: (qual) =>
      qual.isExpired ? (
        <Chip size="small" label={t({ id: "expiredChipLabel" })} />
      ) : null,
    licensureBody: (qual) => qual.licensureBodyName,
    accreditationBody: (qual) => qual.accreditationBodyName,
    submissionAuthority: (qual) => qual.submissionAuthorityName,
    "profession.name": (qual) => qual.profession?.name,
    "verificationStatus.name": (qual) => (
      <SkuQualificationStatusChip value={qual} />
    ),
    courseNumber: (qual) => qual.courseNumber,
    approvalCode: (qual) => qual.approvalCode,
    approvalDate: (qual) => formatDate(qual.approvalDate),
    releaseDate: (qual) => formatDate(qual.releaseDate),
    creditSubmissionExpirationDate: (qual) =>
      formatDate(qual.creditSubmissionExpirationDate),
    credits: (qual) => qual.credits,
    minimumCredit: (qual) => qual.minimumCredit,
    partialCreditIncrement: (qual) => qual.partialCreditIncrement,
  };

  // Mirrors the parent: same columns in the same order, minus the ones the group key
  // pins. The show/hide picker drives both tables because the visible set is read off
  // the live table instance.
  const redundantColumnIds = REDUNDANT_COLUMN_IDS[groupBy] ?? [];
  const visibleColumns: ColumnConfig<SkuQualificationRow>[] = [];
  for (const { id } of table.getVisibleLeafColumns()) {
    const render = redundantColumnIds.includes(id) ? undefined : childCells[id];
    if (render) {
      // The label comes off the column definition: MRT_Column.header is typed as a
      // string but is only populated on header objects, not on the column itself.
      const { header } = table.getColumn(id).columnDef;
      visibleColumns.push({ id, label: header, render });
    }
  }

  const hasNotesVariation = parent.notes === MULTIPLE_VALUES_INDICATOR;

  return (
    <Box sx={{ pl: 6, pr: 2, py: 2 }}>
      <TableContainer component={Paper} elevation={1}>
        {/* width:auto so the columns size to content instead of stretching across the
            parent MRT row width (~2400px), which spaced them far apart. */}
        <Table size="small" sx={{ width: "auto" }}>
          <TableHead>
            <TableRow>
              {hasNotesVariation && (
                <TableCell sx={{ width: 50, fontWeight: "bold" }} />
              )}
              {visibleColumns.map((col) => (
                <TableCell key={col.id} sx={{ fontWeight: "bold" }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {children.map((child) => (
              <ChildRow
                key={child.id}
                child={child}
                visibleColumns={visibleColumns}
                hasNotes={hasNotesVariation}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {parent.notes && parent.notes !== MULTIPLE_VALUES_INDICATOR && (
        <Paper sx={{ mt: 2, p: 2 }} elevation={1}>
          <Box sx={styles.parentNotes}>
            <strong>Notes:</strong> {parent.notes}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
